package br.com.duoma.duomaproducao.viewmodel

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.graphics.Color
import br.com.duoma.duomaproducao.ui.components.Filter
import com.patrykandpatrick.vico.compose.cartesian.data.CartesianChartModelProducer
import com.patrykandpatrick.vico.compose.cartesian.data.CartesianValueFormatter
import com.patrykandpatrick.vico.compose.cartesian.data.lineSeries
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.launch

data class LineChartSeries<Datapoint>(
    val series : Map<Datapoint,Double>,
    val legend : String,
    val color : Color
)

class LineChartManager <Datapoint> internal constructor (
    datapoints : List<Datapoint>,
    val datapointToString : (Datapoint) -> String,
    private val _onToggleFilter : ((Datapoint, Map<Datapoint,Boolean>) -> Map<Datapoint,Boolean>)?,
    val reorderable: Boolean,
    initialSeries : List<LineChartSeries<Datapoint>>
) {
    val modelProducer = CartesianChartModelProducer()
    var legends : List<Pair<String, Color>> by mutableStateOf(initialSeries.map { it.legend to it.color })
    val filterList by derivedStateOf { datapointsOrder.map { it to (enabledDatapoints[it] ?: false) } }
    val onToggleFilter : ((Datapoint) -> Unit)? = if (_onToggleFilter != null) {{ i ->
            enabledDatapoints = _onToggleFilter(i, enabledDatapoints)
            coroutineScope.launch {
                update()
            }
        }} else null
    val coroutineScope = CoroutineScope(Dispatchers.Main)
    var enabledDatapoints by mutableStateOf(datapoints.associateWith { true })
        private set
    var datapointsOrder by mutableStateOf(datapoints)
        private set
    var currentSeries : List<LineChartSeries<Datapoint>> by mutableStateOf(initialSeries)
        private set
    var bottomLabelFormatter by mutableStateOf(makeFormatter())

    private fun makeFormatter() = CartesianValueFormatter { _, v, _ ->
        val enabled = datapointsOrder.filter { enabledDatapoints[it] ?: false }
        val item = enabled.getOrElse(v.toInt()) { datapointsOrder.firstOrNull() }
            ?: return@CartesianValueFormatter "-"
        val label = datapointToString(item)
        if (label.isBlank()) "-" else label
    }

    val onMove = if (reorderable) ::_onMove else null
    private fun _onMove(from : Int, to : Int) {
        datapointsOrder = datapointsOrder.toMutableList().apply {
            add(to, removeAt(from))
        }
        coroutineScope.launch {
            update()
        }
    }
    suspend fun updateSeries(series : List<LineChartSeries<Datapoint>>) {
        legends = series.map {
            it.legend to it.color
        }
        currentSeries = series
        update()
    }
    private suspend fun update() {
        val sorted = currentSeries.map {series ->
            datapointsOrder.filter { enabledDatapoints[it] ?: false }.map {
                series.series[it] ?: 0.0
            }
        }
        if (sorted.isNotEmpty())
            modelProducer.runTransaction {
                lineSeries {
                    sorted.forEach {
                        series(it)
                    }
                }
            }

        bottomLabelFormatter = makeFormatter()
    }
}

@Composable
fun <K,Datapoint> rememberLineChartManager(
    key : K,
    series : (K) -> List<LineChartSeries<Datapoint>>,
    datapoints : List<Datapoint>,
    datapointToString : (Datapoint) -> String,
    onToggle : ((Datapoint, Map<Datapoint,Boolean>) -> Map<Datapoint,Boolean>)?,
    reorderable: Boolean
) : LineChartManager<Datapoint> {
    val manager = remember {
        LineChartManager(
            datapoints,
            datapointToString,
            onToggle,
            reorderable,
            series(key)
        )
    }
    LaunchedEffect(key) {
        manager.updateSeries(series(key))
    }
    return manager
}